import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  FeatureRow,
  StatBand,
  Accordion,
  CTASection,
  Reveal,
  Card,
  Badge,
  Icon,
  Mock,
} from './kit.jsx'

const CONTROLS = [
  {
    icon: 'lock',
    title: 'AWS Cognito authentication',
    body: 'Sign-in runs on Amazon Cognito user pools with hosted, hardened login. Sessions are backed by short-lived, signed JWTs — no passwords ever touch our application code.',
  },
  {
    icon: 'shield',
    title: 'Per-user data isolation',
    body: 'Every request is scoped to the caller. Identity comes only from a verified Cognito ID token — never a field in the request body — so a user can read and write their own mailbox, sequences, and pipeline, and no one else’s.',
    tag: 'Zero cross-tenant',
  },
  {
    icon: 'globe',
    title: 'Encryption in transit',
    body: 'All traffic to the app and API is served over TLS 1.2+. Plaintext HTTP is redirected and HSTS is enforced at the edge, so credentials and lead data are never exposed on the wire.',
  },
  {
    icon: 'boxes',
    title: 'Encryption at rest',
    body: 'Every DynamoDB table holding your leads, sequences, and activity is encrypted at rest with AWS-managed keys. The 10,942-company dataset and all of your outreach live on encrypted storage by default.',
  },
  {
    icon: 'target',
    title: 'Least-privilege IAM',
    body: 'Each Lambda and service runs under a scoped IAM role that can touch only the specific tables and actions it needs. No shared admin credentials, no broad wildcards on production data.',
  },
  {
    icon: 'zap',
    title: 'CloudFront edge delivery',
    body: 'The app is distributed through CloudFront with TLS termination, edge caching, and AWS Shield DDoS protection in front of every origin request.',
  },
  {
    icon: 'refresh',
    title: 'PITR backups & deletion protection',
    body: 'Data tables run with point-in-time recovery and deletion protection enabled, so records can be restored to any second in the retention window and tables can’t be dropped by accident.',
    tag: 'Point-in-time',
  },
  {
    icon: 'mail',
    title: 'Google OAuth for Gmail',
    body: 'Sending happens through Google OAuth with read and send scopes only. We never ask for your Google password, we don’t store it, and you can revoke access from your Google account at any time.',
  },
  {
    icon: 'receipt',
    title: 'Audit event log',
    body: 'Sign-ins, sequence sends, pipeline changes, and Gmail connections are written to an append-only activity log so you always have a trail of who did what, and when.',
  },
]

const TRUST_STATS = [
  { value: 'TLS 1.2+', label: 'Encryption in transit, everywhere' },
  { value: 'AES-256', label: 'DynamoDB encryption at rest' },
  { value: 'Read + Send', label: 'The only Gmail scopes we request' },
  { value: 'In progress', label: 'SOC 2 Type II' },
]

const FAQS = [
  {
    q: 'Who owns the data in my workspace?',
    a: 'You do. Your leads, sequences, pipeline, and the messages you send from your own Gmail belong to you. We process this data only to provide Revailo Sales, never to sell it or train third-party models. The underlying 10,942-company directory is licensed commercial data made available to every account.',
  },
  {
    q: 'Which subprocessors touch my data?',
    a: 'Revailo Sales runs entirely on Amazon Web Services (Cognito, Lambda, DynamoDB, CloudFront, S3) in U.S. regions, and uses Google (Gmail API) to send and read replies for the mailbox you connect. We keep a current subprocessor list and provide it, along with our DPA, on request from security@revailo-sales.com.',
  },
  {
    q: 'How is my Gmail access secured, and can I revoke it?',
    a: 'We connect to Gmail through Google OAuth using the read and send scopes only — we never receive your Google password. Tokens are stored encrypted and scoped to your user. You can disconnect inside Revailo Sales or revoke access at any time from your Google Account security settings, which immediately stops all sending.',
  },
  {
    q: 'What happens when I delete data or close my account?',
    a: 'Deleting a sequence, contact, or your account removes the associated records from our production tables. Point-in-time recovery snapshots roll off on their retention schedule, after which the data is unrecoverable. On written request we will purge remaining copies and confirm completion.',
  },
  {
    q: 'What is your process if there is a security incident?',
    a: 'We monitor for anomalous activity and maintain an incident-response runbook. In the event of a confirmed breach affecting your data, we will investigate, contain, and notify affected account owners without undue delay, along with the facts we have and the steps we are taking. Our system status is published at /status.',
  },
  {
    q: 'Are you SOC 2 certified?',
    a: 'Honestly: not yet. Our SOC 2 Type II program is in progress and we are building against the Trust Services Criteria. We are happy to walk security and compliance teams through our current controls and share evidence in the meantime — reach out and we will set up a review.',
  },
]

export default function SecurityPage() {
  return (
    <MarketingShell active="security">
      <Seo
        title="Security"
        description="How Revailo Sales protects your leads, outreach, and Gmail access — AWS Cognito auth, per-user data isolation, encryption in transit and at rest, least-privilege IAM, PITR backups, and an audit event log."
        path="/security"
        keywords="Revailo Sales security, sales CRM security, AWS Cognito, DynamoDB encryption, Gmail OAuth security, SOC 2"
      />

      <Section>
        <PageHero
          eyebrow="Security"
          title={
            <>
              Your leads and your inbox, <br className="hidden sm:block" />
              handled the way you’d handle them.
            </>
          }
          lead="Revailo Sales runs on AWS with verified identity, per-user isolation, and encryption end to end. Your outreach sends from your own Gmail — with the narrowest access we can ask for, revocable anytime."
          primary={{ label: 'Talk to security', to: '/contact' }}
          secondary={{ label: 'Read the FAQ', to: '/security#faq' }}
        />
      </Section>

      <Section className="py-[56px]">
        <Container>
          <Reveal>
            <StatBand stats={TRUST_STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="controls">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Controls"
              title="The controls behind every account"
              lead="Concrete, verifiable safeguards — not a vague promise. Here is exactly how your workspace is protected on AWS."
            />
          </Reveal>
          <div className="mt-[56px]">
            <Reveal>
              <FeatureGrid items={CONTROLS} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section id="gmail">
        <Container>
          <Reveal>
            <FeatureRow
              eyebrow="Gmail security model"
              title="You send as yourself — we never hold the keys to your inbox"
              body="Outreach goes out through Google OAuth, not a shared blast server. You grant read and send access to one mailbox, tokens are stored encrypted and scoped to your user, and identity on every send is proven by your Cognito token — never a mailbox address passed in a request."
              bullets={[
                'Read + send scopes only — no contacts, no Drive, no account settings',
                'We never see or store your Google password',
                'Tokens encrypted at rest and isolated per user',
                'Revoke from Revailo Sales or your Google Account at any time',
              ]}
              media={
                <Reveal>
                  <Mock kind="inbox" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section className="py-[56px]">
        <Container>
          <Reveal>
            <Card soft>
              <div className="flex items-center justify-between">
                <Badge tone="accent">Compliance</Badge>
              </div>
              <div className="m-0 mt-[16px] text-[26px] font-semibold leading-[1.15] tracking-[-0.022em] text-ink" style={{ fontSize: 22 }}>
                SOC 2 Type II — in progress
              </div>
              <p className="m-0 mt-[8px] text-[14px] leading-[1.6] text-dim" style={{ maxWidth: 720 }}>
                We’re not going to claim a badge we haven’t earned. Revailo Sales is actively
                building its SOC 2 Type II program against the Trust Services Criteria. Until it’s
                complete, we’re glad to walk your security team through our current controls,
                architecture, and DPA. Email <strong>security@revailo-sales.com</strong> to start a review.
              </p>
            </Card>
          </Reveal>
        </Container>
      </Section>

      <Section id="faq">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Security FAQ"
              title="Straight answers for security teams"
              lead="Data ownership, subprocessors, deletion, and how we respond when something goes wrong."
            />
          </Reveal>
          <div className="mt-[40px]" style={{ maxWidth: 820, margin: '40px auto 0' }}>
            <Reveal>
              <Accordion items={FAQS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Security"
        title="Have a security or compliance question?"
        body="Send it to our team and we’ll walk you through our controls, subprocessors, and DPA."
        primary={{ label: 'Talk to security', to: '/contact' }}
        secondary={{ label: 'View system status', to: '/status' }}
      />
    </MarketingShell>
  )
}
